import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { FiChevronLeft, FiRefreshCw } from 'react-icons/fi';
import { discoveryApi } from '../../api/discovery';
import PageLayout from '../../components/layout/PageLayout';
import AppContentSection from '../../app-ui/AppContentSection';
import ContentShelf, { ShelfNavigationContext } from '../../components/responsive/ContentShelf';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { AccountBoundary, useSettings } from '../../features/settings/shared';
import { useMemberEntry } from '../../features/members/memberMemory';
import { restoreSearchScroll } from '../../features/search/useSearchScrollMemory';
import SEO from '../../components/common/SEO';
import { useUserStore } from '../../stores/userStore';
import TimelineCard from './TimelineCard';
import {
  decades,
  timelineFilters,
  timelineItems,
  timelineNext,
  timelineTitle,
  timelinePath,
  loadTimelineOverview,
} from './timelineModel';
import './Timeline.css';
export { timelineFilters } from './timelineModel';
const cacheOptions = {
  retry: false,
  staleTime: Infinity,
  gcTime: 30 * 60_000,
  refetchOnWindowFocus: false,
  refetchOnMount: true,
};
export default function Timeline() {
  return <AccountBoundary component={TimelineBoundary} />;
}
function TimelineBoundary({ user }) {
  const { locale } = useWebLocale();
  const memory = useMemberEntry(`timeline60:${locale}`);
  if (useUserStore.getState().user?.id !== user?.id) return null;
  return <TimelineView key={`${locale}:${memory.entry.version}`} {...memory} user={user} />;
}
function PeriodRail({ label, options, value, onChange, underline = false }) {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current,
      selected = node?.querySelector('[aria-pressed="true"]');
    if (!selected) return;
    const center = () =>
      node.scrollTo?.({
        left: Math.max(
          0,
          selected.offsetLeft - node.offsetLeft + (selected.offsetWidth - node.clientWidth) / 2,
        ),
        behavior: 'auto',
      });
    center();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(center);
    observer?.observe(node);
    return () => observer?.disconnect();
  }, [value]);
  return (
    <div
      ref={ref}
      role="group"
      aria-label={label}
      className={`pp-timeline-period ${underline ? 'pp-timeline-decades' : 'pp-timeline-years'}`}
    >
      {options.map((option) => (
        <button
          type="button"
          key={option.value}
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
function TimelineView({ entry, returning, user }) {
  const { locale, t } = useWebLocale(),
    { style, en } = useSettings(),
    navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filters = timelineFilters(params),
    root = useRef(null),
    more = useRef(null),
    busy = useRef(false),
    restoring = useRef(returning && entry.y !== undefined);
  const [restoreEpoch, setRestoreEpoch] = useState(0);
  const lastEntry = useRef(entry);
  if (lastEntry.current !== entry) {
    lastEntry.current = entry;
    restoring.current = returning && entry.y !== undefined;
  }
  const overview = useQuery({
    ...cacheOptions,
    queryKey: [
      'web-parity',
      'timeline-preview',
      'overview60',
      locale,
      user?.id || 'guest',
      entry.version,
      filters.decade,
      filters.year,
    ],
    enabled: !filters.section,
    queryFn: ({ signal }) => loadTimelineOverview(discoveryApi.timeline, filters, signal),
  });
  const list = useInfiniteQuery({
    ...cacheOptions,
    queryKey: [
      'web-parity',
      'timeline-all',
      'section60',
      locale,
      user?.id || 'guest',
      entry.version,
      filters,
    ],
    enabled: !!filters.section,
    initialPageParam: 1,
    queryFn: ({ signal, pageParam }) =>
      discoveryApi.timeline({ ...filters, page: pageParam, size: 20 }, signal),
    getNextPageParam: (page) => timelineNext(page, filters.section),
  });
  const query = filters.section ? list : overview;
  const items = timelineItems(list.data?.pages || [], filters),
    sections = (overview.data || []).filter((s) => s.items.length);
  const ready = !query.isPending;
  useEffect(() => {
    if (!ready || !restoring.current) return;
    let alive = true,
      cancel;
    const timer = setTimeout(() => {
      if (!entry.current()) return;
      for (const rail of root.current?.querySelectorAll('.pp-shelf-track') || [])
        rail.scrollLeft = entry.rails?.[rail.getAttribute('aria-label')] || 0;
      cancel = restoreSearchScroll(entry.y || 0, () => {
        if (!alive) return;
        restoring.current = false;
        setRestoreEpoch((n) => n + 1);
        if (entry.current() && entry.focus)
          Array.from(root.current?.querySelectorAll('a') || [])
            .find((a) => a.getAttribute('href') === entry.focus)
            ?.focus({ preventScroll: true });
      });
    }, 0);
    return () => {
      alive = false;
      clearTimeout(timer);
      cancel?.();
    };
  }, [entry, ready]);
  useEffect(() => {
    const save = () => {
      if (entry.current() && !restoring.current) entry.y = window.scrollY;
    };
    window.addEventListener('scroll', save, { passive: true });
    return () => window.removeEventListener('scroll', save);
  }, [entry]);
  const next = async () => {
    if (busy.current || list.isFetching || !list.hasNextPage) return;
    busy.current = true;
    try {
      await list.fetchNextPage({ cancelRefetch: false });
    } finally {
      busy.current = false;
    }
  };
  const nextRef = useRef(next);
  nextRef.current = next;
  useEffect(() => {
    if (
      !filters.section ||
      !more.current ||
      !list.hasNextPage ||
      list.isFetching ||
      list.isError ||
      typeof IntersectionObserver === 'undefined'
    )
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !document.hidden && !restoring.current)
          void nextRef.current();
      },
      { rootMargin: '480px' },
    );
    const observe = () => {
      observer.disconnect();
      if (!document.hidden && more.current) observer.observe(more.current);
    };
    observe();
    document.addEventListener('visibilitychange', observe);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', observe);
    };
  }, [filters.section, list.hasNextPage, list.isFetching, list.isError, restoreEpoch]);
  const remember = (event) => {
    const link = event.target.closest('a');
    if (!link || !entry.current()) return;
    entry.y = window.scrollY;
    entry.focus = link.getAttribute('href');
    entry.rails = Object.fromEntries(
      Array.from(root.current.querySelectorAll('.pp-shelf-track')).map((node) => [
        node.getAttribute('aria-label'),
        node.scrollLeft,
      ]),
    );
  };
  const update = (key, value) => {
    const nextParams = new URLSearchParams();
    nextParams.set('decade', key === 'decade' ? value : filters.decade);
    if (key === 'year' && value !== 'all') nextParams.set('year', value);
    setParams(nextParams, { replace: true });
  };
  const retry = () =>
    list.isFetchNextPageError && filters.section ? next() : query.refetch({ cancelRefetch: false });
  const empty = filters.section ? !items.length : !sections.length;
  return (
    <PageLayout paddingTop={{ xs: '16px', lg: '24px' }}>
      <section
        ref={root}
        className="pp-settings pp-timeline"
        style={style}
        onClickCapture={remember}
      >
        <SEO title={t('timeline')} />
        <header className="pp-settings-header pp-timeline-header">
          <button
            aria-label={en ? 'Back' : '뒤로'}
            onClick={() =>
              window.history.state?.idx > 0
                ? navigate(-1)
                : navigate(
                    filters.section
                      ? timelinePath({ decade: filters.decade, year: filters.year })
                      : '/explore',
                  )
            }
          >
            <FiChevronLeft size={22} />
          </button>
          <h1>{t('timeline')}</h1>
          <button
            className="pp-timeline-refresh"
            aria-label={en ? 'Refresh list' : '목록 새로고침'}
            disabled={query.isFetching}
            onClick={() => query.refetch({ cancelRefetch: false })}
          >
            <FiRefreshCw size={18} />
          </button>
        </header>
        {!filters.section && (
          <div className="pp-timeline-filters">
            <PeriodRail
              label={t('era')}
              underline
              value={filters.decade}
              onChange={(v) => update('decade', v)}
              options={decades.map((value) => ({ value, label: `${value}${en ? 's' : '년대'}` }))}
            />
            <PeriodRail
              label={t('year')}
              value={filters.year || 'all'}
              onChange={(v) => update('year', v)}
              options={[
                { value: 'all', label: t('all') },
                ...Array.from(
                  {
                    length: Math.max(
                      0,
                      Math.min(10, new Date().getFullYear() - filters.decade + 1),
                    ),
                  },
                  (_, i) => ({
                    value: filters.decade + i,
                    label: `${filters.decade + i}${en ? '' : '년'}`,
                  }),
                ),
              ]}
            />
          </div>
        )}
        {query.isPending && <p role="status">{t('loading')}…</p>}
        {query.isError && (
          <div role="alert" className="pp-timeline-state">
            <p>{t('error')}</p>
            <button disabled={query.isFetching} onClick={retry}>
              {t('retry')}
            </button>
          </div>
        )}
        {ready && !query.isError && empty && (
          <div className="pp-timeline-state">
            <p>
              {filters.section
                ? en
                  ? 'No albums match these filters'
                  : '조건에 맞는 앨범이 없습니다'
                : en
                  ? 'No Postpop records for this period yet.'
                  : '아직 이 기간의 포스트팝 기록이 없어요.'}
            </p>
            <p>{en ? 'Try another decade or year.' : '연대나 연도를 바꿔 확인해 주세요.'}</p>
          </div>
        )}
        {filters.section ? (
          <AppContentSection title={timelineTitle(filters, locale)}>
            <div className="pp-timeline-grid">
              {items.map((item) => (
                <TimelineCard
                  key={item.album.id}
                  item={item}
                  rediscovered={filters.section === 'rediscovered'}
                />
              ))}
            </div>
          </AppContentSection>
        ) : (
          <ShelfNavigationContext.Provider value="edges">
            <div className="pp-timeline-sections">
              {sections.map(({ filter, items: selected }) => (
                <AppContentSection
                  key={filter.region + filter.section}
                  title={timelineTitle(filter, locale)}
                  to={timelinePath(filter)}
                  inlineMore
                >
                  {filter.section === 'opening' ? (
                    <div className="pp-timeline-grid">
                      {selected.map(({ item }) => (
                        <TimelineCard key={item.album.id} item={item} />
                      ))}
                    </div>
                  ) : (
                    <ContentShelf
                      label={timelineTitle(filter, locale)}
                      className="pp-timeline-shelf"
                    >
                      {selected.map(({ item, reason }) => (
                        <TimelineCard
                          key={item.album.id}
                          item={item}
                          reason={reason}
                          rediscovered
                        />
                      ))}
                    </ContentShelf>
                  )}
                </AppContentSection>
              ))}
            </div>
          </ShelfNavigationContext.Provider>
        )}
        {filters.section && (
          <div ref={more} className="pp-timeline-more">
            {list.isFetchingNextPage ? (
              <p role="status">{t('loading')}…</p>
            ) : list.hasNextPage && !list.isError ? (
              <button disabled={list.isFetching} onClick={next}>
                {t('loadMore')}
              </button>
            ) : null}
          </div>
        )}
      </section>
    </PageLayout>
  );
}
